if (!customElements.get('print-bundle')) {
  customElements.define(
    'print-bundle',
    class PrintBundle extends HTMLElement {
      connectedCallback() {
        this.max = parseInt(this.dataset.max) || 3;
        this.pickerTitle = this.querySelector('.print-bundle__picker-title')?.textContent.trim() || '';
        this.tiers = JSON.parse(this.dataset.tiers || '[]').sort((a, b) => a.quantity - b.quantity);
        this.items = [];
        this.slotsEl = this.querySelector('.print-bundle__slots');
        this.compareEl = this.querySelector('.print-bundle__compare');
        this.totalEl = this.querySelector('.print-bundle__total');
        this.submitButton = this.querySelector('.print-bundle__submit');
        this.errorEl = this.querySelector('.print-bundle__error');
        this.picker = this.querySelector('.print-bundle__picker');
        this.moneyFormat = new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency: this.dataset.currency,
        });

        this.boundOnDocumentClick = this.onDocumentClick.bind(this);
        this.boundOnKeydown = this.onKeydown.bind(this);

        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('change', this.onVariantSelect.bind(this));
        this.submitButton.addEventListener('click', this.onSubmit.bind(this));
        document.addEventListener('click', this.boundOnDocumentClick);
        document.addEventListener('keydown', this.boundOnKeydown);

        // Pre-fill the first slot with the print being viewed.
        const currentCard = this.getCard(this.dataset.currentProduct);
        if (currentCard) this.addItem(currentCard, this.dataset.currentVariant, true);

        if (typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data.sectionId !== this.dataset.section || !data.variant) return;
            this.onCurrentVariantChange(data.variant);
          });
        }

        this.render();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        document.removeEventListener('click', this.boundOnDocumentClick);
        document.removeEventListener('keydown', this.boundOnKeydown);
      }

      openPicker(opener) {
        this.openerButton = opener;
        this.picker.hidden = false;
        this.querySelectorAll('.print-bundle__slot-add').forEach((button) =>
          button.setAttribute('aria-expanded', button === opener ? 'true' : 'false')
        );
      }

      closePicker({ restoreFocus = true } = {}) {
        if (this.picker.hidden) return;
        this.picker.hidden = true;
        this.querySelectorAll('.print-bundle__slot-add').forEach((button) =>
          button.setAttribute('aria-expanded', 'false')
        );
        if (restoreFocus) this.openerButton?.focus();
      }

      onDocumentClick(event) {
        if (this.picker.hidden) return;
        if (this.picker.contains(event.target) || event.target.closest('.print-bundle__slot-add')) return;
        this.closePicker({ restoreFocus: false });
      }

      onKeydown(event) {
        if (event.key === 'Escape' && !this.picker.hidden) this.closePicker();
      }

      getCard(productId) {
        if (!productId) return null;
        return this.querySelector(`.print-bundle__row[data-product-id="${productId}"]`);
      }

      getVariantData(card, variantId) {
        const select = card.querySelector('.print-bundle__variant');
        const option =
          (variantId && select.querySelector(`option[value="${variantId}"]:not([disabled])`)) ||
          select.selectedOptions[0];
        if (!option || option.disabled) return null;

        return {
          variantId: option.value,
          variantTitle: option.dataset.title,
          price: parseInt(option.dataset.price),
        };
      }

      addItem(card, variantId, isCurrent = false) {
        if (this.items.length >= this.max) return;
        const variant = this.getVariantData(card, variantId);
        if (!variant) return;

        this.items.push({
          ...variant,
          isCurrent,
          productId: card.dataset.productId,
          title: card.dataset.title,
          image: card.dataset.image,
          singleVariant: card.querySelector('.print-bundle__variant').classList.contains('hidden'),
        });
      }

      onCurrentVariantChange(variant) {
        const current = this.items.find((item) => item.isCurrent);
        if (!current) return;

        if (!variant.available) {
          this.items = this.items.filter((item) => item !== current);
        } else {
          const data = this.getVariantData(this.getCard(current.productId), variant.id);
          if (data) Object.assign(current, data);
        }
        this.render();
      }

      onVariantSelect(event) {
        if (!event.target.classList.contains('print-bundle__variant')) return;

        const row = event.target.closest('.print-bundle__row');
        const priceEl = row.querySelector('.print-bundle__row-price');
        const price = parseInt(event.target.selectedOptions[0]?.dataset.price);
        if (priceEl && !Number.isNaN(price)) priceEl.textContent = this.formatMoney(price);
      }

      onClick(event) {
        if (event.target.closest('.print-bundle__close')) {
          this.closePicker();
          return;
        }

        const slotButton = event.target.closest('.print-bundle__slot-add');
        const addButton = event.target.closest('.print-bundle__add');
        const removeButton = event.target.closest('.print-bundle__remove');

        if (slotButton) {
          if (this.picker.hidden) {
            this.openPicker(slotButton);
          } else {
            this.closePicker();
          }
        } else if (addButton) {
          this.addItem(addButton.closest('.print-bundle__row'));
          this.render();
          this.openerButton = this.querySelector('.print-bundle__slot-add') || this.submitButton;
          this.closePicker({ restoreFocus: this.picker.contains(document.activeElement) });
        } else if (removeButton) {
          this.items.splice(parseInt(removeButton.dataset.index), 1);
          this.render();
          this.querySelector('.print-bundle__slot-add')?.focus();
        }
      }

      getDiscountPercent(count) {
        return Math.max(0, ...this.tiers.filter((tier) => count >= tier.quantity).map((tier) => tier.percent));
      }

      escape(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML.replaceAll('"', '&quot;');
      }

      formatMoney(cents) {
        return this.moneyFormat.format(cents / 100);
      }

      render() {
        const count = this.items.length;
        const percent = this.getDiscountPercent(count);
        const subtotal = this.items.reduce((sum, item) => sum + item.price, 0);
        const total = Math.round(subtotal * (1 - percent / 100));

        // Show 2 slots by default; reveal one more as each is filled, up to the max.
        const visibleSlots = Math.min(this.max, Math.max(2, count + 1));
        this.slotsEl.style.setProperty('--slots', visibleSlots);

        this.slotsEl.innerHTML = Array.from({ length: visibleSlots }, (_, index) => {
          const item = this.items[index];
          if (!item) {
            return `<li class="print-bundle__slot">
              <button type="button" class="print-bundle__slot-add" aria-haspopup="listbox" aria-expanded="false" aria-controls="${this.picker.id}">
                <span class="print-bundle__slot-plus" aria-hidden="true">+</span>
                <span class="visually-hidden">${this.escape(this.pickerTitle)}</span>
              </button>
            </li>`;
          }
          const title = this.escape(item.title);
          const variantLabel = item.singleVariant
            ? ''
            : `<span class="print-bundle__slot-variant">${this.escape(item.variantTitle)}</span>`;
          return `<li class="print-bundle__slot">
            <div class="print-bundle__media">
              ${item.image ? `<img src="${this.escape(item.image)}" alt="" loading="lazy" width="400" height="400">` : ''}
              <button type="button" class="print-bundle__remove" data-index="${index}" aria-label="Remove ${title}">&times;</button>
            </div>
            <span class="print-bundle__slot-title">${title}</span>
            ${variantLabel}
          </li>`;
        }).join('');

        this.compareEl.textContent = percent > 0 ? this.formatMoney(subtotal) : '';
        this.totalEl.textContent = count > 0 ? this.formatMoney(total) : '';

        this.submitButton.disabled = count < (this.tiers[0]?.quantity || 2);
      }

      onSubmit() {
        if (this.submitButton.disabled) return;

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const quantities = this.items.reduce((map, item) => {
          map[item.variantId] = (map[item.variantId] || 0) + 1;
          return map;
        }, {});
        const body = {
          items: Object.entries(quantities).map(([id, quantity]) => ({ id: parseInt(id), quantity })),
        };
        if (cart?.tagName === 'CART-DRAWER') {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement(this.submitButton);
        }

        this.errorEl.hidden = true;
        this.submitButton.classList.add('loading');
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.querySelector('.loading__spinner')?.classList.remove('hidden');

        fetch(`${routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.errorEl.textContent = response.description || response.message;
              this.errorEl.hidden = false;
              return;
            }

            if (cart?.tagName !== 'CART-DRAWER') {
              window.location = window.routes.cart_url;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'print-bundle', cartData: response });
            cart.classList.remove('is-empty');
            cart.renderContents(response);
          })
          .catch((error) => console.error(error))
          .finally(() => {
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            this.submitButton.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }
    }
  );
}
